import { i18n } from '../../i18n/i18n'

import React, { Component, RefObject } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { Transformer as HTMLTransformerElement } from 'konva/lib/shapes/Transformer'
import { RootState } from '../../store'
import {
  resetState,
  replaceId,
  replaceMarkup,
  replaceActive,
  resetElements,
  clearSelectedElement,
  replaceCloning,
  Markup,
  Gs1BarcodeElement,
  groupElements,
  ungroupElements
} from '../../views/LabelBuilder/LabelBuilder.slice'
import labelApi, {
  LabelCreateRequest,
  LabelSnapshotRequest,
  LabelUpdateRequest
} from '../../apis/label.api'
import gs1Api from '../../apis/gs1.api'
import { ConfirmParams } from '../Confirm/Confirm.state'
import confirmService from '../../services/confirm.service'
import Button from '../Button'
import Card from '../Card'

import html2canvas from 'html2canvas'
import { Promise } from 'bluebird'
import markupService from '../../services/markup.service'
import { jsPDF } from 'jspdf'
import storageService from '../../services/storage.service'
import routes from '../../routes/routes'

interface RouteState {
    customLabelId?: number
    systemLabelId?: number
}

interface Props {
    transformerRef: RefObject<HTMLTransformerElement>
    handleImageClick: (image: string, labelId?: number) => void
}

type CombinedProps = Props
    & RouteComponentProps<any, any, RouteState>
    & ConnectedProps<typeof connector>

interface DownloadOption {
    label: string;
    value: 'png' | 'jpeg' | 'svg' | 'eps' | 'zpl' | 'pdf';
}

const downloadOptions: DownloadOption[] = [
  { label: 'PNG', value: 'png' },
  { label: 'JPEG', value: 'jpeg' },
  { label: 'SVG', value: 'svg' },
  { label: 'EPS', value: 'eps' },
  { label: 'ZPL', value: 'zpl' },
  { label: 'PDF', value: 'pdf' }
]

interface State {
    showPopup: boolean;
    showTrialAlert: boolean;
}

class Actions extends Component<CombinedProps, State> {
  constructor (props: CombinedProps) {
    super(props)

    this.state = {
      showPopup: false,
      showTrialAlert: false
    }

    this.handlePreviewClick = this.handlePreviewClick.bind(this)
    this.handleSaveClick = this.handleSaveClick.bind(this)
    this.handleActivateClick = this.handleActivateClick.bind(this)
    this.handleDownloadLabel = this.handleDownloadLabel.bind(this)
    this.togglePopup = this.togglePopup.bind(this)
    this.toggleTrialAlertPopup = this.toggleTrialAlertPopup.bind(this)
    this.handleCancelTrialAlert = this.handleCancelTrialAlert.bind(this)
    this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
  }

  get cancelButton () {
    const { state } = this.props.location
    if (!state) return
    const { customLabelId } = state
    return customLabelId &&
            <Button
                type='secondary'
                text={i18n('Cancel')}
                onClick={this.handleCancelClick}/>
  }

  handleCancelClick = () => {
    this.props.history.goBack()
  }

  handleDeleteLabelConfirm (id: number) {
    labelApi.delete(id, label => {
      if (!label) return
      this.props.resetState()
    })
  }

  handleDeleteLabelClick = () => {
    const { id } = this.props
    if (!id) return
    const confirm: ConfirmParams = {
      id,
      type: 'warning',
      question: i18n('Are you sure to delete the label?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleDeleteLabelConfirm(id)
    }
    confirmService.push(confirm)
  }

  handlePreviewClick = async () => {
    const { state } = this.props.location
    if (!state) return
    const { customLabelId } = state
    const payload = { markup: this.props.markup }

    labelApi.preview(payload, async (markup) => {
      if (!markup) return
      this.props.handleImageClick(markup?.snapshot, customLabelId ?? undefined)
    })
  }

  create (active: boolean) {
    const { markup } = this.props
    const payload: LabelCreateRequest = { active, markup }
    labelApi.create(payload, label => {
      if (!label) return
      const { id, markup, active } = label
      this.props.replaceId(id)
      this.props.replaceMarkup(markup)
      this.props.replaceActive(active)

      this.createSnapshotImageAndUpload(markup, id)
    })
  }

  async htmlToCanvas (element: HTMLElement) {
    if (element) {
      const canvas = await html2canvas(element)
      const image = canvas.toDataURL('image/png')
      return image
    }
    return null
  }

  async createSnapshotImageAndUpload(markup: Markup, id: number) {

    labelApi.previewAttributes(id, async (attributes) => {

      let finalMarkup = markup

      // Only replace if API attributes exist
      if (attributes && attributes.api_field && attributes.sample_data) {
        finalMarkup = this.replaceMarkupVariables(markup, attributes)
      }

      const mainElement = document.createElement('div')
      const labelElement = document.createElement('div')

      const imageElement = await markupService.toImageElement(finalMarkup)

      if (!imageElement) return

      imageElement.style.width = 'calc(100% - 1px)'
      imageElement.style.height = 'calc(100% - 1px)'

      labelElement.appendChild(imageElement)
      mainElement.appendChild(labelElement)

      document.documentElement.appendChild(mainElement)

      const imageData = await this.htmlToCanvas(mainElement)

      mainElement.remove()

      if (imageData) {
        const payload: LabelSnapshotRequest = { image: imageData, id }

        labelApi.saveLabelSnapshot(payload, () => {
          console.log('Snapshot API successful')
        })
      }

    })
  }

  replaceMarkupVariables(markup: Markup, attributes: any): Markup {

    const apiAttributes = this.parseApiAttributes(attributes)

    const newMarkup = JSON.parse(JSON.stringify(markup))

    newMarkup.elements = newMarkup.elements.map((el: any) => {

      if (!el.text) return el

      Object.keys(apiAttributes).forEach(key => {

        const variable = `api:${key}`

        if (el.text.includes(variable)) {
          el.text = el.text.replaceAll(variable, apiAttributes[key])
        }

      })
      return el
    })

    return newMarkup
  }

  parseApiAttributes(attributes: any) {
    const parseArrayValue = (value: string[] | string | undefined) => {
      if (!value) return []
      if (Array.isArray(value)) return value

      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (error) {
        console.error('Error parsing preview API attributes:', error)
        return []
      }
    }

    const fields = parseArrayValue(attributes.api_field)
    const samples = parseArrayValue(attributes.resolved_sample_data ?? attributes.sample_data)

    const map: Record<string, string> = {}

    fields.forEach((field: string, index: number) => {
      map[field] = samples[index] || ""
    })

    return map
  }

  togglePopup = () => {
    this.setState((prevState) => ({ showPopup: !prevState.showPopup }))
  }

  toggleTrialAlertPopup = () => {
    this.setState((prevState) => ({ showTrialAlert: !prevState.showTrialAlert }))
  }

  downloadLabel (format: string) {
    const { markup } = this.props
    switch (format) {
      case 'png':
        this.createSnapshotImageAndDownload(markup, 1, 'png')
        break
      case 'jpeg':
        this.createSnapshotImageAndDownload(markup, 1, 'jpeg')
        break
      case 'svg':
        this.createSnapshotImageAndDownload(markup, 1, 'svg')
        break
      case 'eps':
        this.createSnapshotImageAndDownload(markup, 1, 'eps')
        break
      case 'zpl':
        this.createSnapshotImageAndDownload(markup, 1, 'zpl')
        break
      case 'pdf':
        this.createSnapshotImageAndDownload(markup, 1, 'pdf')
        break
      default:
        break
    }
  }

  // Start download funcionality
  async createSnapshotImageAndDownload (markup: Markup, id: number, format: 'png' | 'jpeg' | 'svg' | 'eps' | 'zpl' | 'pdf' = 'png') {
    const mainElement = document.createElement('div')
    const labelElement = document.createElement('div')
    const imageElement = await markupService.toImageElement(markup)

    if (!imageElement) return

    imageElement.style.width = 'calc(100% - 1px)'
    imageElement.style.height = 'calc(100% - 1px)'
    labelElement.appendChild(imageElement)
    mainElement.appendChild(labelElement)

    document.documentElement.appendChild(mainElement)

    if (format === 'pdf') {
      const canvas = await html2canvas(mainElement, { scale: 1 })
      mainElement.remove()

      if (canvas) {
        const dpi = 203

        let widthInches = Number(localStorage.getItem('canvasWidth'))
        let heightInches = Number(localStorage.getItem('canvasHeight'))
        const unit = localStorage.getItem('canvasUnit')

        if (unit === 'mm') {
          widthInches /= 25.4
          heightInches /= 25.4
        } else if (unit === 'px') {
          widthInches /= dpi
          heightInches /= dpi
        }

        const pdfWidth = widthInches * 72
        const pdfHeight = heightInches * 72

        const imageData = canvas.toDataURL('image/png')
        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
          unit: 'pt',
          format: [pdfWidth, pdfHeight]
        })

        pdf.addImage(imageData, 'PNG', 0, 0, pdfWidth, pdfHeight)
        pdf.save(`snapshot_${id}.pdf`)
      }
    } else if (format === 'zpl') {
      const u = localStorage.getItem('canvasUnit')
      let w = Number(localStorage.getItem('canvasWidth'))
      let h = Number(localStorage.getItem('canvasHeight'))

      // if(u ==  'in'){}
      if (u == 'mm') {
        w = w / 25.4
        h = h / 25.4
      }
      if (u == 'px') {
        w = w / 203
        h = h / 203
      }

      let zplData = ''
      try {
        zplData = await this.convertToZPLUsingAPI(mainElement, w, h, markup)
      } finally {
        mainElement.remove()
      }
      if (zplData) {
        const blob = new Blob([zplData], { type: 'text/plain;charset=utf-8' })
        this.downloadFile(blob, `snapshot_${id}.zpl`)
      }
    } else if (format === 'svg' || format === 'eps') {
      const canvas = await html2canvas(mainElement, { backgroundColor: null })
      mainElement.remove()

      if (canvas) {
        const dpi = 96

        let widthInches = Number(localStorage.getItem('canvasWidth'))
        let heightInches = Number(localStorage.getItem('canvasHeight'))
        const unit = localStorage.getItem('canvasUnit')

        if (unit === 'mm') {
          widthInches /= 25.4
          heightInches /= 25.4
        } else if (unit === 'px') {
          widthInches /= dpi
          heightInches /= dpi
        }

        const svgWidth = widthInches * dpi
        const svgHeight = heightInches * dpi

        const imageData = canvas.toDataURL('image/png')

        const svg = `<?xml version="1.0" encoding="UTF-8"?>
       <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
         <image href="${imageData}" width="${svgWidth}" height="${svgHeight}"/>
       </svg>`

        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })

        if (format === 'svg') {
          this.downloadFile(blob, `snapshot_${id}.svg`)
        } else {
          const epsData = `!PS-Adobe-3.0 EPSF-3.0\n${svg}`
          const epsBlob = new Blob([epsData], { type: 'application/postscript' })
          this.downloadFile(epsBlob, `snapshot_${id}.eps`)
        }
      }
    } else {
      const canvas = await html2canvas(mainElement)
      mainElement.remove()

      if (canvas) {
        const imageData =
                    format === 'jpeg'
                      ? canvas.toDataURL('image/jpeg', 0.9)
                      : canvas.toDataURL('image/png')

        const blob = this.dataURLToBlob(imageData)
        const fileExtension = format === 'jpeg' ? 'jpg' : 'png'
        this.downloadFile(blob, `snapshot_${id}.${fileExtension}`)
      }
    }
  }

  // new
  async convertToZPLUsingAPI (element: HTMLElement, widthInInches: number = 4, heightInInches: number = 6, markup?: Markup): Promise<string> {
    const canvas = await html2canvas(element, { backgroundColor: null })

    if (!canvas) {
      throw new Error('Failed to generate canvas from element')
    }

    const maxSize = 200 * 1024 // 200 KB in bytes
    const maxPixels = 2000 // Maximum pixel dimension
    const dpi = 203 // Fixed DPI (dots per inch)

    // Convert inches to pixels
    const targetWidth = widthInInches * dpi
    const targetHeight = heightInInches * dpi

    // Compute scaling ratio to fit within specified dimensions while preserving aspect ratio
    const scaleX = targetWidth / canvas.width
    const scaleY = targetHeight / canvas.height
    const scale = Math.min(scaleX, scaleY, 1) // Prevent upscaling

    const resizedCanvas = document.createElement('canvas')
    resizedCanvas.width = Math.floor(canvas.width * scale)
    resizedCanvas.height = Math.floor(canvas.height * scale)

    const ctx = resizedCanvas.getContext('2d')
    if (!ctx) {
      throw new Error('Failed to get 2D context for resized canvas')
    }

    ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height)

    let imageData = resizedCanvas.toDataURL('image/png')
    let blob = this.dataURLToBlob(imageData)

    let compressionRatio = 1
    while (blob.size > maxSize && compressionRatio > 0.1) {
      compressionRatio -= 0.1
      imageData = resizedCanvas.toDataURL('image/jpeg', compressionRatio)
      blob = this.dataURLToBlob(imageData)
    }

    if (blob.size > maxSize) {
      throw new Error('Image could not be compressed below size limit')
    }

    const formData = new FormData()
    formData.append('file', blob, 'image.jpg')

    const response = await fetch('https://api.labelary.com/v1/graphics', {
      method: 'POST',
      headers: {
        Accept: 'application/zpl'
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Failed to convert image to ZPL: ${response.statusText}`)
    }

    const zplData = await response.text()
    return this.injectRfidZpl(zplData, markup)
  }

  plainTextToRfidHex (content: string): string {
    const bytes = new TextEncoder().encode(content)
    let hex = Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()

    const remainder = hex.length % 4
    if (remainder !== 0) {
      hex += '0'.repeat(4 - remainder)
    }

    return hex
  }

  buildRfidZpl (markup?: Markup): string {
    const commands = (markup?.elements || [])
      .filter(element => element.type === 'rfid')
      .map(element => {
        const content = element.text.trim()
        if (!content) return ''

        const hex = this.plainTextToRfidHex(content)
        const byteCount = hex.length / 2

        return `^RFW,H,2,${byteCount},1\n^FD${hex}^FS`
      })
      .filter(Boolean)

    return commands.join('\n')
  }

  /**
   * Tag writes for GS1 Barcodes that encode the RFID tag.
   *
   * The EPC is derived from the barcode's data by the server - the same
   * value the GS1 panel displays - so this asks the GS1 endpoint for it
   * rather than reimplementing EPC encoding here. A barcode whose EPC
   * cannot be built contributes no command, which is what the print route
   * does too.
   */
  async gs1RfidZpl (markup?: Markup): Promise<string> {
    const writers = (markup?.elements || []).filter(
      element => element.type === 'gs1_barcode' && element.gs1?.rfid?.enabled
    ) as Gs1BarcodeElement[]
    if (!writers.length) return ''

    const dpi = markup?.stage?.dpi || 203
    const commands = await Promise.all(writers.map(element => (
      new Promise<string>(resolve => {
        gs1Api.check(
          { gs1: element.gs1, dpi },
          result => {
            const hex = result?.epc?.hex
            // ^RS8 selects Gen 2 tags; ^RFW,H writes the EPC bank and lets
            // the printer set the PC length bits, as the print route does.
            resolve(hex ? `^RS8\n^RFW,H^FD${hex}^FS` : '')
          },
          () => resolve('')
        )
      })
    )))

    return commands.filter(Boolean).join('\n')
  }

  async injectRfidZpl (zpl: string, markup?: Markup): Promise<string> {
    const rfidZpl = [this.buildRfidZpl(markup), await this.gs1RfidZpl(markup)]
      .filter(Boolean)
      .join('\n')
    if (!rfidZpl) return zpl

    if (zpl.includes('^XA')) {
      return zpl.replace(/\^XA\s*/, `^XA\n${rfidZpl}\n`)
    }

    return `^XA\n${rfidZpl}\n${zpl.trim()}\n^XZ`
  }

  dataURLToBlob (dataURL: string): Blob {
    const byteString = atob(dataURL.split(',')[1])
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0]
    const arrayBuffer = new Uint8Array(byteString.length)

    for (let i = 0; i < byteString.length; i++) {
      arrayBuffer[i] = byteString.charCodeAt(i)
    }

    return new Blob([arrayBuffer], { type: mimeString })
  }

  downloadFile (blob: Blob, filename: string) {
    const downloadLink = document.createElement('a')
    const url = URL.createObjectURL(blob)

    downloadLink.href = url
    downloadLink.download = filename
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)

    URL.revokeObjectURL(url)
  }

  // End download functionality

  update (id: number, active: boolean) {
    const { markup } = this.props
    const payload: LabelUpdateRequest = { id, active, markup }
    labelApi.update(id, payload, label => {
      if (!label) return
      const { markup, active } = label
      this.props.replaceMarkup(markup)
      this.props.replaceActive(active)

      this.createSnapshotImageAndUpload(markup, id)
    })
  }

  handleSaveClick () {
    const { id, markup } = this.props
    const active = false
    if (id) this.update(id, active)
    else this.create(active)
  }

  handleDownloadLabel (format: string) {
    this.togglePopup()

    if (storageService.get('inTrial') == true) {
      this.toggleTrialAlertPopup()
      return
    }

    const { id, markup } = this.props
    console.log('Downloading PNG....' + JSON.stringify(markup))
    this.downloadLabel(format)
  }

  handleCancelTrialAlert () {
    this.toggleTrialAlertPopup()
  }

  handleSubscribeClick () {
    this.toggleTrialAlertPopup()
    this.props.history.push(routes.userSubscriptions)
  }

  handleActivateClick () {
    const { id } = this.props
    const active = true
    if (id) this.update(id, active)
    else this.create(active)
  }

  handleClearElementsClick = () => {
    const confirm: ConfirmParams = {
      id: 0,
      type: 'warning',
      question: i18n('Are you sure to clear all elements?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.props.resetElements()
    }
    confirmService.push(confirm)
  }

  handleClearElementConfirm = () => {
    this.props.clearSelectedElement()
    const transformerNode = this.props.transformerRef.current
    if (!transformerNode) return
    transformerNode.nodes([])
  }

  handleClearElementClick = () => {
    const confirm: ConfirmParams = {
      id: 0,
      type: 'warning',
      question: i18n('Are you sure to clear the element?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleClearElementConfirm()
    }
    confirmService.push(confirm)
  }

  handleCloneLabelClick = () => {
    this.props.replaceCloning(true)
  }

  /* NEW: call group / ungroup */
  handleGroupClick = () => {
    this.props.groupElements()
    const transformerNode = this.props.transformerRef.current
    if (transformerNode) {
      transformerNode.nodes([]) // will be re-attached by Template when store updates
    }
  }

  handleUngroupClick = () => {
    this.props.ungroupElements()
    const transformerNode = this.props.transformerRef.current
    if (transformerNode) {
      transformerNode.nodes([])
    }
  }

  render () {
    const { showPopup, showTrialAlert } = this.state
    const selCount = this.props.selectedElements ? this.props.selectedElements.length : 0
    const isGroupSelected = this.props.isGroupSelected

    return (
            <Card background="white" flat={true}>
                <div className='flex flex-wrap justify-end gap-2'>
                    {selCount > 0 && <Button
                        type='secondary'
                        text={i18n('Clear element')}
                        onClick={this.handleClearElementClick}/>}

                    {selCount > 1 && (
                        <Button
                            type='secondary'
                            text={'Group'}
                            onClick={this.handleGroupClick}/>
                    )}

                    {selCount === 1 && isGroupSelected && (
                        <Button
                            type='secondary'
                            text={'Ungroup'}
                            onClick={this.handleUngroupClick}/>
                    )}

                    {!selCount &&
                        <div className='flex flex-wrap justify-end gap-2'>
                            <Button
                                type='danger'
                                text={i18n('Delete label')}
                                onClick={this.handleDeleteLabelClick}/>
                            <Button
                                type='secondary'
                                text={i18n('Clear elements')}
                                onClick={this.handleClearElementsClick}/>
                            <Button
                                type='secondary'
                                text={i18n('Preview')}
                                onClick={this.handlePreviewClick}/>
                            <Button
                                type='secondary'
                                text={i18n('Save draft')}
                                onClick={this.handleSaveClick}/>
                            <Button
                                text={i18n('Save and activate')}
                                onClick={this.handleActivateClick}/>
                            <Button
                                type='secondary'
                                text={i18n('Clone label')}
                                onClick={this.handleCloneLabelClick}/>
                            <Button
                                type="secondary"
                                text={i18n('Download')}
                                onClick={this.togglePopup}
                            />

                            {showPopup && (
                                <>
                                    <div
                                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                                        onClick={this.togglePopup}
                                    ></div>

                                    <div className="fixed inset-0 flex items-center justify-center z-50">
                                        <div className="bg-white shadow-lg w-80 p-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                                Choose Format
                                            </h3>
                                            <ul className="space-y-2">
                                                {downloadOptions
                                                  .map(option => (
                                                        <li key={option.value}>
                                                            <button
                                                                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                                onClick={() => this.handleDownloadLabel(option.value)}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        </li>
                                                  ))}
                                            </ul>
                                            <button
                                                className="mt-4 w-full bg-gray-300 text-gray-700 py-2 hover:bg-gray-400"
                                                onClick={this.togglePopup}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showTrialAlert && (
                                <>
                                    <div
                                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                                        onClick={this.handleCancelTrialAlert}
                                    ></div>
                                    <div className="fixed inset-0 flex items-center justify-center z-50">
                                        <div className="bg-white shadow-lg w-80 p-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                                Subscription Required
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-4">{i18n('To download this format, you need a paid subscription.')}</p>
                                            <div className="flex gap-2">
                                                <button
                                                    className="bg-blue-600 text-white px-4 py-2 "
                                                    onClick={this.handleSubscribeClick}
                                                >
                                                    Subscribe
                                                </button>
                                                <button
                                                    className="bg-gray-300 text-gray-700 px-4 py-2 "
                                                    onClick={this.handleCancelTrialAlert}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {this.cancelButton}
                        </div>
                    }
                </div>
            </Card>
    )
  }
}

function mapStateToProps (state: RootState) {
  const labelBuilderState = state.labelBuilder
  const { markup, id } = labelBuilderState
  const { selectedElements } = markup

  let isGroupSelected = false
  if (selectedElements && selectedElements.length === 1) {
    const el = markup.elements.find(e => e.id === selectedElements[0])
    if (el && (el as any).type === 'group') isGroupSelected = true
  }
  return {
    markup,
    selectedElements,
    id,
    isGroupSelected
  }
}

const mapDispatchToProps = {
  resetState,
  resetElements,
  replaceId,
  replaceMarkup,
  clearSelectedElement,
  replaceActive,
  replaceCloning,
  groupElements,
  ungroupElements
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(withRouter(Actions))
